import { Box, Button, Flex, Menu, MenuButton, MenuItem, MenuList } from '@chakra-ui/react';
import { ChevronDown, X } from 'lucide-react';
import { useCallback } from 'react';
import upperFirst from 'lodash-es/upperFirst.js';
import { t } from '@lingui/core/macro';
import { ProviderCourseURLParams } from './types';
import { iconSize } from '@duke-hq/ui/constants/scalable';

interface FilterOption {
  value: string;
  label: string;
  element?: React.ReactElement;
}

export function ProviderCoursesFilters({
  filters,
  statusOptions,
  kindOptions,
  onChange,
}: {
  filters: ProviderCourseURLParams;
  statusOptions: FilterOption[];
  kindOptions: FilterOption[];
  onChange: (newFilters: ProviderCourseURLParams) => void;
}): React.ReactElement {
  const { kind, status } = filters;

  const handleFilterChange = useCallback(
    (key: keyof ProviderCourseURLParams, value: any) => {
      onChange({ ...filters, [key]: value });
    },
    [filters, onChange],
  );

  const formatFilterTitle = (label: string) => {
    return upperFirst(label.replace(/_/g, ' ').toLowerCase());
  };

  return (
    <Flex gap={2}>
      {Array.isArray(kindOptions) && kindOptions.length > 0 ? (
        <Menu>
          <MenuButton
            as={Button}
            variant="outline"
            px={2}
            size="sm"
            width="auto"
            colorScheme="gray"
            rightIcon={
              kind ? (
                <Box
                  p={0}
                  minW="fit-content"
                  onClick={(e) => {
                    e.preventDefault();
                    handleFilterChange('kind', undefined);
                  }}
                  ml={1}
                >
                  <X size={iconSize.md} />
                </Box>
              ) : (
                <ChevronDown size={iconSize.md} />
              )
            }
          >
            {(kind && formatFilterTitle(kind)) || t`Kind`}
          </MenuButton>
          <MenuList>
            {kindOptions.map((kind) => (
              <MenuItem
                key={kind.value}
                onClick={() => {
                  handleFilterChange('kind', kind.value);
                }}
              >
                {(kind as { label: string })?.label}
              </MenuItem>
            ))}
          </MenuList>
        </Menu>
      ) : null}

      {Array.isArray(statusOptions) && statusOptions.length > 0 ? (
        <Menu>
          <MenuButton
            as={Button}
            variant="outline"
            px={2}
            size="sm"
            width="auto"
            colorScheme="gray"
            rightIcon={
              status ? (
                <Box
                  p={0}
                  minW="fit-content"
                  onClick={(e) => {
                    e.preventDefault();
                    handleFilterChange('status', undefined);
                  }}
                  ml={1}
                >
                  <X size={iconSize.md} />
                </Box>
              ) : (
                <ChevronDown size={iconSize.md} />
              )
            }
          >
            {(status && formatFilterTitle(status)) || t`Status`}
          </MenuButton>
          <MenuList>
            {statusOptions.map((status) => (
              <MenuItem
                key={status.value}
                onClick={() => {
                  handleFilterChange('status', status.value);
                }}
              >
                {(status as { label: string })?.label}
              </MenuItem>
            ))}
          </MenuList>
        </Menu>
      ) : null}
    </Flex>
  );
}
